/** Map a `time_type` code to a short label rendered inside TimeTypeBadge. */
export function timeTypeLabel(timeType: string): string {
  switch (timeType) {
    case "A":
      return "ACT";
    case "E":
      return "EST";
    case "S":
      return "SCH";
    default:
      return timeType;
  }
}

/** Render a UTC ms timestamp as `9:24 AM` in the user's locale. */
export function formatTime(timestampMs: number): string {
  const date = new Date(timestampMs);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/** Render a UTC ms timestamp as `May 4, 9:24 AM` in the user's locale. */
export function formatDateTime(timestampMs: number): string {
  const date = new Date(timestampMs);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Parse a pier filter string like "40-60, 72, 85-90" into a Set of
 * individual pier number strings. Returns an empty set if the input
 * is blank (meaning "show all").
 */
export function parsePierFilter(input: string): ReadonlySet<string> {
  const trimmed = input.trim();
  if (!trimmed) return new Set<string>();

  const result = new Set<string>();
  for (const part of trimmed.split(",")) {
    const segment = part.trim();
    if (!segment) continue;

    const rangeParts = segment.split("-").map((s) => s.trim());
    if (rangeParts.length === 2) {
      const start = parseInt(rangeParts[0], 10);
      const end = parseInt(rangeParts[1], 10);
      if (!isNaN(start) && !isNaN(end)) {
        const lo = Math.min(start, end);
        const hi = Math.max(start, end);
        for (let i = lo; i <= hi; i++) {
          result.add(String(i));
        }
        continue;
      }
    }

    result.add(segment);
  }
  return result;
}

/** Render a UTC ms timestamp as `in 1h 12m` / `5m ago`, relative to now. */
export function relativeTime(timestampMs: number): string {
  const now = Date.now();
  const diffMs = timestampMs - now;
  const absDiffMs = Math.abs(diffMs);
  const totalMinutes = Math.floor(absDiffMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

  const label = parts.join(" ");
  return diffMs >= 0 ? `in ${label}` : `${label} ago`;
}
